import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { AuthenticationService } from './common/services/authentication.service';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { environment } from './environments/environment';
import { ThemeService } from './theme.service';
import { ConfigurationService } from './common/services/configuration.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  lastPing: Date = new Date();
  idleState = 'Not started.';
  timedOut = false;

  constructor(
    private authenticationService: AuthenticationService,
    private idle: Idle,
    private keepalive: Keepalive,
    private themeService: ThemeService,
    private configService: ConfigurationService,
    private router: Router
  ) {
    this.configureIdleSession();
  }

  ngOnInit(): void {
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      take(1)
    ).subscribe(event => {
      if (environment.dedicatedCss) return;
      const url = this.router.url;
      const isLandingPage = url === '/' || url.startsWith('/landing') || url.includes('new-landing-page');
      if (isLandingPage) {
        this.themeService.loadTheme('');
      } else {
        this.loadThemeForSavedClient();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private checkAndApplyFallbackTheme(): void {
  // Removed checkAndApplyFallbackTheme and all fallback/forceFallbackTheme/sessionStorage logic
  // removed extra closing brace here
  }

  /**
   * Load client-specific theme on app initialization or refresh
   */
  private loadThemeForSavedClient(): void {
    const savedClient = localStorage.getItem('selectedClient');   
    if (savedClient && savedClient !== 'Choose Client') {
      // Load theme from API - this gets fresh SAS token   
      this.configService.getLogo(savedClient).subscribe(
        (result: any) => {
          if (result.fileName && result.url) {
            const logoUrl = result.url;         
            // Build Azure theme URL from logo URL
            const queryIndex = logoUrl.indexOf('?');
            const urlWithoutQuery = queryIndex > 0 ? logoUrl.substring(0, queryIndex) : logoUrl;
            const sasToken = queryIndex > 0 ? logoUrl.substring(queryIndex) : '';
            const lastSlashIndex = urlWithoutQuery.lastIndexOf('/');
            if (lastSlashIndex > 0) {
              const secondLastSlashIndex = urlWithoutQuery.lastIndexOf('/', lastSlashIndex - 1);
              if (secondLastSlashIndex > 0) {
                const baseUrl = urlWithoutQuery.substring(0, secondLastSlashIndex);
                const themeUrl = `${baseUrl}/css/theme.css${sasToken}`;
                // Load theme using ThemeService with fresh SAS token
                this.themeService.loadTheme(themeUrl);
              }
            }
          }
        },
        (error: any) => {
          console.error('[ThemeDebug] Error fetching logo from API for client:', savedClient, error);
          // Load fallback theme on error
          this.themeService.loadTheme('');
        }
      );
    } else {     
      this.themeService.loadTheme('');
    }
  }

  /**
   * Configures the idle session monitoring
   */
  private configureIdleSession(): void {
    // Set the idle timeout based on environment configuration (converted to seconds)
    this.idle.setIdle(Number(environment.sessionTimeOut) * 60);

    // Set the timeout period after idle is detected
    this.idle.setTimeout(5);

    // Set the default interrupts for idle detection
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    // Configure the logout action when idle timeout occurs
    this.idle.onTimeout.subscribe(() => {
      this.timedOut = true;
      this.authenticationService.logout();
    });

    // Configure the keepalive interval in seconds
    this.keepalive.interval(15);

    // Update the last ping time when a ping is received
    this.keepalive.onPing.subscribe(() => {
      this.lastPing = new Date();
    });

    // Start watching for user activity
    this.reset();
  }

  /**
   * Resets the idle watch timer
   */
  reset(): void {
    this.idle.watch();
    this.idleState = 'Started';
    this.timedOut = false;
  }
}
